import { createContext, useContext, type ReactNode } from 'react'
import type { PricingQuote, PricingQuoteResult } from '~/utils/pricingQuote'

/**
 * Shared context holding the latest live pricing quote for whatever product is
 * being configured (wardrobe, stand, …). Producers fire usePricingQuoteSync and
 * publish the result here. The stored value is the full request+response pair
 * (PricingQuoteResult); `useQuote()` exposes just the response for the pricing
 * UI, while `useQuoteResult()` exposes the pair for the add-to-cart path.
 *
 * Default `undefined` = no quote provider in the tree (a static-priced product).
 * `null` = quote-driven product, still awaiting the first response.
 */
const QuoteContext = createContext<PricingQuoteResult | null | undefined>(
  undefined
)

export function QuoteProvider({
  value,
  children,
}: {
  value: PricingQuoteResult | null
  children: ReactNode
}) {
  return <QuoteContext.Provider value={value}>{children}</QuoteContext.Provider>
}

/** The response only — what the pricing UI renders (total, breakdown). */
export function useQuote(): PricingQuote | null | undefined {
  const result = useContext(QuoteContext)
  if (result === undefined) return undefined
  if (result === null) return null
  return result.response
}

/** The full request+response pair — stored on the cart item at add time. */
export function useQuoteResult(): PricingQuoteResult | null | undefined {
  return useContext(QuoteContext)
}
